/**
 * Canonical stage labels and badge colors for the 360 OS deal lifecycle.
 * All portals (Borrower, Admin, Lender, Investor) must use these constants
 * to ensure consistent stage display across the platform.
 */

export const STAGE_LABELS = {
  new_lead: "New Lead",
  submitted: "Submitted",
  in_review: "In Review",
  needs_info: "Needs Info",
  underwriting: "Underwriting",
  approved: "Approved",
  capital_matching: "Capital Matching",
  terms: "Terms",
  funding: "Funding",
  funded: "Funded",
  active: "Active",
  declined: "Declined",
  // Legacy stages
  intake: "Intake",
  intake_complete: "Intake Complete",
  docs_requested: "Docs Requested",
  packaging: "Packaging",
  matched: "Matched",
  submitted_to_lender: "Submitted to Lender",
  terms_received: "Terms Received",
  conditional_approval: "Conditional Approval",
  clear_to_close: "Clear to Close",
};

export const STAGE_BADGE_COLORS = {
  new_lead: "bg-gray-100 text-gray-600",
  submitted: "bg-blue-50 text-blue-600",
  in_review: "bg-amber-50 text-amber-600",
  needs_info: "bg-orange-50 text-orange-600",
  underwriting: "bg-purple-50 text-purple-600",
  approved: "bg-emerald-50 text-emerald-600",
  capital_matching: "bg-indigo-50 text-indigo-600",
  terms: "bg-violet-50 text-violet-600",
  funding: "bg-teal-50 text-teal-600",
  funded: "bg-emerald-100 text-emerald-700",
  active: "bg-green-100 text-green-700",
  declined: "bg-red-100 text-red-600",
};

/** Returns the canonical label for a stage, with a Title Case fallback. */
export function stageLabel(stage) {
  if (!stage) return "Unknown";
  return STAGE_LABELS[stage] || stage.replace(/_/g, " ").replace(/\b\w/g, l => l.toUpperCase());
}

/**
 * STAGE_DESCRIPTIONS — borrower-facing descriptions of what each stage means,
 * what's happening behind the scenes, and typical timeline.
 */
export const STAGE_DESCRIPTIONS = {
  submitted: {
    title: "Application Submitted",
    description: "Your deal has been received. Our AI underwriting engine is performing an initial analysis of your property, financials, and market data.",
    whatsNext: "An intake specialist will review your submission within 1 business day.",
    avgDays: 1,
  },
  in_review: {
    title: "Under Review",
    description: "Our intake team is verifying your deal details and may request additional information or documents.",
    whatsNext: "Ensure all required documents are uploaded to avoid delays.",
    avgDays: 2,
  },
  underwriting: {
    title: "In Underwriting",
    description: "Our underwriting team is evaluating your deal structure, risk profile, and financial projections. An AI-powered risk assessment is being generated.",
    whatsNext: "You'll receive a decision once underwriting is complete.",
    avgDays: 3,
  },
  approved: {
    title: "Approved",
    description: "Congratulations — your deal has been approved! We're now preparing your deal for the lending marketplace.",
    whatsNext: "Lenders will begin reviewing your deal shortly.",
    avgDays: 1,
  },
  capital_matching: {
    title: "Capital Matching",
    description: "Your deal is being presented to our network of qualified lenders. We're matching based on loan type, geography, and deal size.",
    whatsNext: "Watch for lender interest and term sheet offers.",
    avgDays: 5,
  },
  terms: {
    title: "Term Sheets Received",
    description: "One or more lenders have submitted term sheets for your deal. Review the offers and select the best fit.",
    whatsNext: "Compare offers and select your preferred lender.",
    avgDays: 3,
  },
  funding: {
    title: "In Funding",
    description: "Your deal is in the final closing process. Wire instructions and closing documents are being coordinated.",
    whatsNext: "Your Imaani representative will guide you through closing.",
    avgDays: 7,
  },
  funded: {
    title: "Funded",
    description: "Your deal has been funded! Capital has been deployed and your loan is now active.",
    whatsNext: "Monitor your loan in the Servicing tab.",
    avgDays: 0,
  },
  declined: {
    title: "Declined",
    description: "Unfortunately, we were unable to match your deal at this time. Our team may follow up with alternative options.",
    whatsNext: "Contact your representative for alternative strategies.",
    avgDays: 0,
  },
  needs_info: {
    title: "Information Requested",
    description: "The review team needs additional information or documents from you before proceeding.",
    whatsNext: "Check your open requests and respond promptly to avoid delays.",
    avgDays: 0,
  },
};

/** Returns the badge color class for a stage. */
export function stageBadgeColor(stage) {
  return STAGE_BADGE_COLORS[stage] || "bg-gray-100 text-gray-600";
}
